import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { MAX_ZOOM, MIN_ZOOM, ORIGIN, ZOOM_STEP, clampPan, clampZoom, wheelZoom, zoomAtPoint, type Point } from './viewport';

/**
 * Zoom / rotate / pan for a photo viewer, with mouse-wheel zoom toward the pointer and drag-to-pan (L-1212).
 * `areaRef` is the viewer's photo area: the wheel is listened to natively (non-passive) there so the page behind never scrolls,
 * and drag starts on it. Rotation is always 0/90/180/270; rotating or changing photo returns to fit.
 */
export function usePhotoViewport(
  areaRef: RefObject<HTMLElement | null>,
  opts: { dragEnabled?: boolean; wheelEnabled?: boolean; /** The area is mounted (it may render nothing while content is missing); re-attaches the wheel listener. */ ready?: boolean } = {},
) {
  const { dragEnabled = true, wheelEnabled = true, ready = true } = opts;
  const [zoom, setZoomState] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState<Point>(ORIGIN);
  const zoomRef = useRef(1);
  const panRef = useRef<Point>(ORIGIN);
  zoomRef.current = zoom;
  panRef.current = pan;
  const drag = useRef<{ from: Point; pan: Point } | null>(null);
  const [dragging, setDragging] = useState(false);

  /** The pan, held inside the viewing area (see clampPan). */
  const bounded = useCallback((p: Point): Point => {
    const r = areaRef.current?.getBoundingClientRect();
    return clampPan(p, zoomRef.current, r?.width ?? 0, r?.height ?? 0);
  }, [areaRef]);

  /** Change zoom (about the viewer centre unless a pointer is given). Back at fit or below, the photo is centred again. */
  const zoomTo = useCallback((next: number, pointer?: Point) => {
    const to = clampZoom(next);
    const from = zoomRef.current;
    const rect = areaRef.current?.getBoundingClientRect();
    const centre: Point = rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : ORIGIN;
    setPan((p) => (to <= 1 ? ORIGIN : zoomAtPoint(p, from, to, pointer ?? centre, centre)));
    setZoomState(to);
  }, [areaRef]);

  const zoomIn = useCallback(() => zoomTo(zoomRef.current + ZOOM_STEP), [zoomTo]);
  const zoomOut = useCallback(() => zoomTo(zoomRef.current - ZOOM_STEP), [zoomTo]);
  const fit = useCallback(() => { setZoomState(1); setPan(ORIGIN); }, []);
  const rotateRight = useCallback(() => { setRotation((r) => (r + 90) % 360); fit(); }, [fit]);
  const rotateLeft = useCallback(() => { setRotation((r) => (r + 270) % 360); fit(); }, [fit]);
  const reset = useCallback(() => { setRotation(0); fit(); }, [fit]);

  // Mouse wheel: zoom toward the pointer. Native + non-passive so preventDefault stops the page behind scrolling.
  useEffect(() => {
    const el = areaRef.current;
    if (!el || !wheelEnabled) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey && !e.deltaY) return;
      // The Details drawer scrolls itself: a wheel over it must not zoom the photo.
      if ((e.target as HTMLElement | null)?.closest?.('[data-photo-meta-drawer]')) return;
      e.preventDefault();
      zoomTo(wheelZoom(zoomRef.current, e.deltaY, e.deltaMode), { x: e.clientX, y: e.clientY });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [areaRef, wheelEnabled, ready, zoomTo]);

  // Drag to pan while zoomed in.
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!dragEnabled || e.button !== 0 || zoomRef.current <= 1) return;
    if ((e.target as HTMLElement).closest?.('button, a, input, textarea, select, [data-photo-meta-drawer]')) return;
    e.preventDefault();
    drag.current = { from: { x: e.clientX, y: e.clientY }, pan: panRef.current };
    setDragging(true);
  }, [dragEnabled]);

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e: MouseEvent) => {
      const d = drag.current;
      if (d) setPan(bounded({ x: d.pan.x + e.clientX - d.from.x, y: d.pan.y + e.clientY - d.from.y }));
    };
    const up = () => { drag.current = null; setDragging(false); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragging, bounded]);

  /** One-finger pan while zoomed in (touch). Returns false when not zoomed so the caller can treat the gesture as a swipe. */
  const touchPanStart = useCallback((x: number, y: number): boolean => {
    if (!dragEnabled || zoomRef.current <= 1) return false;
    drag.current = { from: { x, y }, pan: panRef.current };
    return true;
  }, [dragEnabled]);
  const touchPanMove = useCallback((x: number, y: number) => {
    const d = drag.current;
    if (d && zoomRef.current > 1) setPan(bounded({ x: d.pan.x + x - d.from.x, y: d.pan.y + y - d.from.y }));
  }, [bounded]);
  const touchPanEnd = useCallback(() => { drag.current = null; }, []);

  return {
    zoom, rotation, pan, dragging, canPan: zoom > 1, touchPanStart, touchPanMove, touchPanEnd,
    setZoom: zoomTo, zoomIn, zoomOut, fit, rotateRight, rotateLeft, reset, onMouseDown,
    atMin: zoom <= MIN_ZOOM, atMax: zoom >= MAX_ZOOM,
  };
}

export type PhotoViewport = ReturnType<typeof usePhotoViewport>;
